import { Ref, computed, ComputedRef } from "vue";
import { injectBrand } from "./brand";
import formatCurrency from "./lib/formatCurrency";

export const Defaults = {
  currency: "GBP",
  stake: 10
};

/**
 * Hook to get the stake amount and formatted stake with currency for user language
 * @returns Object
 */
export function useStake(): {
  formattedStake: ComputedRef<string>;
  stakeAmount: ComputedRef<number>;
} {
  const brand = injectBrand();

  const stakeAmount = computed(() => {
    let stake = Defaults.stake;

    if (brand?.potentialWinnings.value) {
      stake = brand.potentialWinnings.value.stake || Defaults.stake;
    }
    return stake;
  });

  const formattedStake = computed(() => {
    let currency = Defaults.currency;

    if (brand?.potentialWinnings.value) {
      currency = brand.potentialWinnings.value.currency;
    }

    return formatCurrency(currency, stakeAmount.value, true);
  });

  return { formattedStake, stakeAmount };
}

/**
 * Hook to decide whether or not to display potenitial winnings
 * @returns Boolean
 */
export function useShowWinnings(): ComputedRef<boolean> {
  const brand = injectBrand();
  const showWinnings = computed(
    () => brand?.oddsDisplayTemplate.value === "potentialWinnings" || false
  );

  return showWinnings;
}
/**
 * Hook takes odds and calculates winnings and formats with currency for user language.
 * @param odds array of numbers
 * @returns Object
 */
function usePotentialWinnings(
  odds: number[]
): {
  winnings: Ref<number>;
  localisedWinnings: Ref<string | null>;
} {
  const brand = injectBrand();
  const { stakeAmount } = useStake();
  const showWinnings = useShowWinnings();

  const winnings = computed(() => {
    const decimal = odds[0] / odds[1] + 1;
    return Math.floor(stakeAmount.value * decimal);
  });

  const localisedWinnings = computed(() => {
    let currency = Defaults.currency;
    if (!showWinnings.value) {
      return null;
    }
    if (brand?.potentialWinnings.value?.currency) {
      currency = brand?.potentialWinnings.value.currency;
    }
    return formatCurrency(currency, winnings.value, false);
  });

  return {
    winnings,
    localisedWinnings
  };
}

export default usePotentialWinnings;
